'use client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  Check,
  Clock,
  Users,
  SoapDispenserDroplet,
  Shield,
  Code,
  Briefcase,
  CheckCircle,
  CircleCheck,
  Bubbles,
  Video,
  FileText,
  Award,
  PenTool,
} from 'lucide-react';
import type { CertificateType } from './certification-wizard';
import { useCertification } from '../contexts/certification-context';
import floors from '../old/assets/Floors.jpg';
import equipment from '../old/assets/equipment.png';
import mix from '../old/assets/mix.png';
import plant from '../old/assets/plant.jpeg';
import graph from '../old/assets/graph.png';
import { Divider, Image } from 'antd';
import { Separator } from 'components/ui/separator';
import Schema from '../old/components/Schema';
import { VideoStep } from './video-step';
import { ContentStep } from './content-step';
import { FinalStep } from './final-step';

export function CertificateSelectionStep() {
  const { selectedCertificate, setSelectedCertificate, currentStep, setCurrentStep } =
    useCertification();

  const certificates: CertificateType[] = [
    {
      id: 'dust-binder-wash',
      title: 'aks2tal Støvbinder',
      description: 'Bliv certificeret i korrekt brug og dosering af støvbinder vask.',
      duration: '10 minutter',
      videoId: 'j3TW1eAF7DQ', // Python tutorial as example
      steps: {
        1: {
          stepTitle: 'Vælg certifikat',
          title: '',
          icon: <Award className="h-4 w-4" />,
          component: <CertificateSelectionStep />,
        },
        2: {
          validation: 'video',
          component: <VideoStep key={2} step={2} />,
          stepTitle: 'Sådan virker støvbinder',
          title: 'Sådan virker støvbinder',
          videoId: 'j3TW1eAF7DQ',
          icon: <Video className="h-4 w-4" />,
          content: (
            <div className="px-6">
              <h2 className="text-lg font-bold">Gulvet som filter</h2>
              <p className="pb-3">
                Når støvbinder anvendes på gulvet reduceres støv i luften, da det bindes til gulvet
                og der dermed skabes et bedre indeklima.
              </p>
              <h2 className="text-lg font-bold">Fordele</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Et produkt til alle vaskbare gulve</p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Mindre støv i luftveje og på overfalder</p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>100% biologisk nedbrydeligt produkt og er PH-neutral</p>
                </li>
              </ul>
            </div>
          ),
        },
        3: {
          validation: 'content',
          component: <ContentStep key={3} step={3} />,
          stepTitle: 'Sådan anvendes støvbinder',
          title: 'Sådan anvendes støvbinder',
          icon: <FileText className="h-4 w-4" />,
          content: (
            <>
              <div className="grid grid-cols-2">
                <div>
                  <Image src={floors} preview={false} className="w-full" />
                </div>
                <div className="px-6">
                  <h2 className="text-lg font-bold py-4">Gulvtyper</h2>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>Et produkt til alle vaskbare gulve</p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        Kan anvendes på trægulv blot det ikke er nylagt og ellers er forbehandlet
                        efter producentens anvisninger.
                      </p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        Aks Støvbinder har en høj plejeeffekt ikke mindst på "åbne" gulve som beton,
                        træ og linolium.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-2">
                <div>
                  <Image src={equipment} preview={false} />
                </div>
                <div className="px-6">
                  <h2 className="text-lg font-bold py-4">Udstyr</h2>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      Kan bruges med moppe
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      Kan bruges med gulvvaskemaskine
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      Udstyret skal blot være rengjort fra tidligere rengøringsmidler og skyllet
                      igennem med rent vand
                    </li>
                  </ul>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-2">
                <div>
                  <Schema />
                </div>
                <div className="px-6">
                  <h2 className="text-lg font-bold py-4">Blandingsforhold</h2>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>Udfra gulvtypen finder du blandingsforholdet</p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        Skulle der ved en fejl bruges for meget støvbinder kan dette løses ved at
                        vaskegulvet med rent vand
                      </p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>Se illustration for eksempel for på bladingsforhold</p>
                    </li>
                  </ul>
                  <Divider>Eksempel: 2% opløsning</Divider>
                  <table style={{ width: '100%' }}>
                    <tr>
                      <td colSpan={2} className="text-center">
                        <Image src={mix} preview={false} height={240} />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          width: '50%',
                          textAlign: 'right',
                          paddingRight: 60,
                        }}
                      >
                        <div>5 liter</div>
                      </td>
                      <td
                        style={{
                          width: '50%',
                          textAlign: 'left',
                          paddingLeft: 25,
                        }}
                      >
                        <div>100 milliter</div>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </>
          ),
        },
        4: {
          validation: 'video',
          component: <VideoStep key={4} step={4} />,
          stepTitle: 'Produkt information',
          title: '100% naturligt produkt',
          icon: <Video className="h-4 w-4" />,
          videoId: 'Sw0Jkpzfi1I',
          content: (
            <div className="px-6">
              <h2 className="text-lg font-bold">Se hvor du kan finde produkt databladet</h2>
              <p className="pb-3">
                Se i videoen til venstre hvor du kan finde produktets datablad. Det vil altid være
                tilgængeligt i aks2tal app'en
              </p>
              <h2 className="text-lg font-bold">Produktet er ikke farligt</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>aks støvbinder er et 100% biologisk nedbrydeligt produkt. og er PH-neutral</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>aks støvbinder er uden duft, farve og konserveringsmiddel.</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>aks støvbinder skummer ikke</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>aks støvbinder har en normal vaskeeffekt.</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>
                    aks støvbinder har en høj plejeeffekt ikke mindst på "åbne" gulve som beton, træ
                    og linolium.
                  </p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>aks støvbinder er 100% vandopløseligt og uden mærkningspligt</p>
                </li>
              </ul>
            </div>
          ),
        },
        5: {
          validation: 'video',
          component: <VideoStep key={5} step={5} />,
          stepTitle: 'Log forbrug',
          title: 'Log forbrug',
          videoId: 'jLBh8Hdonyg',
          icon: <Video className="h-4 w-4" />,
          content: (
            <div className="px-6">
              <h2 className="text-lg font-bold">Forbrug af støvbinder</h2>
              <p className="pb-3">
                Der følger en app med når du bruger AKS Støvbinder. App'en har til formål at
                dokumentere forbruget samt styre beholdningen af Støvbinder. Når der er lav
                beholdning sendes der en besked til leverandøren der sender en ny portion. På den
                måde løber du aldrig tør og skal ikke tænke på at bestille nyt.
              </p>
              <h2 className="text-lg font-bold">Dette skal gøres ved hvert brug</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>
                    Under rengøringslog indtaster du dine initialer og taster den brugte mængde.
                  </p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Der indtastes hver gang du bruger Støvbinder.</p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Se i videoen til venstre hvordan dette nemt gøres i app'en</p>
                </li>
              </ul>
            </div>
          ),
        },
        6: {
          component: <FinalStep />,
          stepTitle: 'Certificeret',
          icon: <PenTool className="h-4 w-4" />,
          title: '',
        },
      },
      requirements: ['Forklaring omkring hvad støvbinder er og hvordan det virker'],
      quizQuestions: [],
    },
    {
      id: 'dust-binder-plant',
      title: 'aks2tal Støvbinder anlæg',
      description: 'Bliv certificeret i korrekt brug og sikkerhed af aks2tal støvbinder anlæg.',
      duration: '10 minutter',
      videoId: 'j3TW1eAF7DQ', // Python tutorial as example
      steps: {
        1: {
          icon: <Award className="h-4 w-4" />,
          component: <CertificateSelectionStep />,
          stepTitle: 'Vælg certifikat',
          title: '',
        },
        2: {
          validation: 'video',
          icon: <Video className="h-4 w-4" />,
          component: <VideoStep key={2} step={2} />,
          videoExplain: (
            <div className="grid grid-cols-2 pt-2">
              <div className="text-center">
                <u>MED</u> AKS STØVBINDER
              </div>
              <div className="text-center">
                <u>UDEN</u> AKS STØVBINDER
              </div>
            </div>
          ),
          stepTitle: 'Sådan virker støvbinder',
          title: 'Sådan virker støvbinder',
          videoId: '0ngldZ3Nv-o',
          content: (
            <div className="px-6">
              <h2 className="text-lg font-bold">Kontrollér støv og partikler</h2>
              <p className="pb-3">
                Støv reduktion af produkter og overflader. Støvbinder partikler og fibre. aks2tal
                reducer støvet effektivt og miljøvenligt
              </p>
              <h2 className="text-lg font-bold">Fordele</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Mindre støv i luftveje og på overflader</p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>Ingen mærkningspligtige råvarer</p>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="h-4 w-4" />
                  <p>100% biologisk nedbrydeligt produkt samt PH-neutralt</p>
                </li>
              </ul>
            </div>
          ),
        },
        3: {
          validation: 'content',
          icon: <FileText className="h-4 w-4" />,
          stepTitle: 'Sådan anvendes støvbinder',
          title: 'Sådan anvendes støvbinder',
          component: <ContentStep key={3} step={3} />,
          content: (
            <>
              <div className="grid grid-cols-2">
                <div>
                  <Image src={plant} preview={false} />
                </div>
                <div className="px-6">
                  <h2 className="text-lg font-bold pt-0 py-4">Kontrollér støv og partikler</h2>

                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>Kan anvendes på alle emner der støver fra foder til cement</p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        Støvbinderen lægger sig om hver partikel og derved bindes/reduceres støvet
                        med op til 98%
                      </p>
                    </li>
                  </ul>

                  <h2 className="text-lg font-bold py-4">Hvor kan man bruge støvbinder</h2>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        Nedknusning af sten, gips, foder, mineraluld, metal, slagger, papirer,
                        fliseklæber, sandprodukter m.m.
                      </p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>AKS Støvbinder påføres materialet via høj- og lavtrykssystemer</p>
                    </li>
                  </ul>
                  <div className="py-4">
                    <p className="text-md">Støvbinder bliver doseret automatisk</p>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        4: {
          validation: 'video',
          icon: <Video className="h-4 w-4" />,
          stepTitle: 'Hvorfor skal man støvbinde',
          title: 'Hvorfor skal man støvbinde',
          videoId: 'Sw0Jkpzfi1I',
          component: <VideoStep key={4} step={4} />,
          content: (
            <div className="px-6">
              <h2 className="text-lg font-bold">Sikkerhed & effekt</h2>
              <p className="">
                Støv er en uundgåelig konsekvens når man producerer, og kan have en lang række
                negative og skadelige virkninger.
              </p>
              <p className="pb-3">
                Se i videoen til venstre, hvor du kan finde produktblade/MSDS. Det vil altid være
                tilgængeligt i AKS X app'en
              </p>
              <h2 className="text-lg font-bold">Fordele</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Støv på slutprodukterne - ofte i form af sorte overflader</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Støv koster timer at fjerne</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>
                    Giver tekniske problemer da støvet f.eks. lægger sig på maskiner, fotoceller, i
                    el-skabe m.m.
                  </p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Øget energiforbrug til ventilation, da man ofte forsøger at suge støvet ud</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Giver et dårligt inde/udeklima</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Er en medvirkende årsag til statisk elektricitet</p>
                </li>
                <li className="flex items-center gap-2">
                  <div>
                    <CircleCheck className="h-4 w-4" />
                  </div>
                  <p>Kan være sundhedsskadeligt f.eks. I form af Kol og kræft</p>
                </li>
              </ul>
              <h2 className="text-lg font-bold pt-4">Produktblade/MSDS </h2>
              <p>
                Læs vedhæftet produktblade/MSDS <u>grundigt</u> igennem.
              </p>
            </div>
          ),
        },
        5: {
          validation: 'content',
          icon: <FileText className="h-4 w-4" />,
          stepTitle: 'Monitorering',
          title: 'Monitorering',
          component: <ContentStep key={5} step={5} />,
          content: (
            <>
              <div className="grid grid-cols-2">
                <div>
                  <Image src={graph} preview={false} />
                </div>
                <div className="px-6">
                  <h2 className="text-lg font-bold pt-0 py-4">Forbrug af støvbinder</h2>
                  <p className="pb-3">
                    Der følger en app med når du bruger AKS Støvbinder. Appen har til formål at
                    dokumentere forbruget. Når der er lav beholdning af AKS Støvbinder sendes der en
                    besked til aks2tal der sender en ny portion AKS Støvbinder. På den måde løber du
                    aldrig tør og skal ikke tænke på selv at bestille.
                  </p>
                  <h2 className="text-lg font-bold pt-0 py-4">Alarmer/notifikationer</h2>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>AKS X app'en kan sende notifikationer ved ureglmæssigheder:</p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        <b>Anlæg ikke sat til opfyldning.</b> Man har glemt at sætte containeren til
                        opfyldning
                      </p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        <b>Doseringsanlægget trækker ikke AKS Støvbinder ind</b>. Årsagen kan være
                        defekt doseringspumpe, "klemt" slange. Undersøg årsagen. Kontakt evt.
                        aks2tal
                      </p>
                    </li>
                    <li className="flex items-center gap-2">
                      <div>
                        <CircleCheck className="h-4 w-4" />
                      </div>
                      <p>
                        <b>AKS Støvbinder niveau er lavt</b> - Der er kun ca. 250 liter AKS
                        Støvbinder tilbage i doseringsanlægget. Fyld tanken op.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </>
          ),
        },
        6: {
          validation: 'content',
          component: <ContentStep key={6} step={6} />,
          icon: <FileText className="h-4 w-4" />,
          stepTitle: 'Vedligeholdelse af anlæg',
          title: 'Vedligeholdelse af anlæg',
          // videoId: 'jLBh8Hdonyg',
          content: (
            <div className="grid grid-cols-2">
              <div>
                <Image src={plant} preview={false} />
              </div>
              <div className="px-6">
                <h2 className="text-lg font-bold">Dagligt</h2>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Kontrollér for utætheder</p>
                  </li>
                </ul>
                <Separator className="my-4" />
                <h2 className="text-lg font-bold">Månedeligt</h2>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>
                      Rengør anlæg. Hvis der bruges trykluft så HUSK brug af passende værnemiddler
                    </p>
                  </li>
                </ul>
                <Separator className="my-4" />
                <h2 className="text-lg font-bold">Påfyld AKS Støvbinder</h2>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Klargør slange fra doseringsanlæg - fjern prop</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Via truck hæves 1.000 liter AKS støvbinder IBC beholder</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Beholder hæves så bunden af beholder er over doseringsanlægget</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Montér slangen til 1.000 liter udløbsventil.</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Åben ventilen langsomt.</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>
                      Hold øje med at der ikke er utætheder f.eks. fordi slangen ikke er spændt
                      korrekt på.
                    </p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Når doseringsanlæg er fyldt afmonteres slangen + "prop" monteres</p>
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4" />
                    <p>Sender tom IBC beholder retur til aks2tal.</p>
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
        7: {
          component: <FinalStep />,
          icon: <PenTool className="h-4 w-4" />,
          stepTitle: 'Certificeret',
          title: '',
        },
      },
      requirements: ['Modtaget en introduktion til støvbinder anlægget'],
      quizQuestions: [],
    },
    // {
    //   id: 'project-management',
    //   title: 'Project Management Essentials',
    //   description:
    //     'Master the fundamentals of project management including planning, execution, and delivery.',
    //   duration: '60 minutes',
    //   videoId: '3qYbVQu7YeQ', // Project management video
    //   steps: {
    //     2: {
    //       title: 'Project Management Fundamentals',
    //     },
    //   },
    //   requirements: [
    //     'Complete the project management fundamentals video',
    //     'Understand project lifecycle and methodologies',
    //     'Learn about resource planning and risk management',
    //     'Master stakeholder communication and reporting',
    //     'Pass the project management assessment with 75% or higher',
    //   ],
    //   quizQuestions: [
    //     {
    //       id: 1,
    //       text: 'What are the main phases of a project lifecycle?',
    //       options: [
    //         { id: 'a', text: 'Start, Middle, End' },
    //         { id: 'b', text: 'Initiation, Planning, Execution, Closure' },
    //         { id: 'c', text: 'Design, Build, Test, Deploy' },
    //         { id: 'd', text: 'Research, Development, Implementation' },
    //       ],
    //       correctAnswer: 'b',
    //     },
    //     {
    //       id: 2,
    //       text: 'What is a project stakeholder?',
    //       options: [
    //         { id: 'a', text: 'Only the project manager' },
    //         { id: 'b', text: 'Anyone affected by or interested in the project' },
    //         { id: 'c', text: 'Only the client paying for the project' },
    //         { id: 'd', text: 'Only the team members working on the project' },
    //       ],
    //       correctAnswer: 'b',
    //     },
    //     {
    //       id: 3,
    //       text: 'What is the purpose of a project charter?',
    //       options: [
    //         { id: 'a', text: 'To track daily tasks' },
    //         { id: 'b', text: 'To formally authorize the project' },
    //         { id: 'c', text: 'To manage the project budget' },
    //         { id: 'd', text: 'To schedule team meetings' },
    //       ],
    //       correctAnswer: 'b',
    //     },
    //   ],
    // },
  ];

  const handleSelect = (certificate: CertificateType) => {
    setSelectedCertificate(certificate);
    setCurrentStep(currentStep + 1);
  };

  const getCertificateIcon = (id: string) => {
    switch (id) {
      case '':
        return <Code className="h-8 w-8 text-blue-600" />;
      case 'dust-binder-plant':
        return <Shield className="h-8 w-8 text-red-600" />;
      case 'project-management':
        return <Briefcase className="h-8 w-8 text-green-600" />;
      default:
        return <Bubbles className="h-8 w-8 text-gray-600" style={{ color: '#2196f3' }} />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Vælg Certificering</h2>
        <p className="text-muted-foreground">Vælg det certifikat du ønsker at tage.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
        {certificates.map((certificate) => (
          <Card
            key={certificate.id}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedCertificate?.id === certificate.id
                ? 'ring-2 ring-primary border-primary bg-primary/5'
                : 'hover:border-primary/50'
            }`}
            onClick={() => handleSelect(certificate)}
          >
            <CardHeader className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getCertificateIcon(certificate.id)}
                  <div>
                    <CardTitle className="text-lg">{certificate.title}</CardTitle>
                  </div>
                </div>
                {selectedCertificate?.id === certificate.id && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
              <CardDescription className="text-sm">{certificate.description}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Varighed: ~{certificate.duration}
                </span>
              </div>

              {certificate.requirements?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Forbedredelse:</h4>
                  <ul className="space-y-1">
                    {certificate.requirements.slice(0, 3).map((requirement, index) => (
                      <li
                        key={index}
                        className="text-xs text-muted-foreground flex items-start space-x-2"
                      >
                        <div className="h-1 w-1 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                        <span>{requirement}</span>
                      </li>
                    ))}
                    {certificate.requirements.length > 3 && (
                      <li className="text-xs text-muted-foreground">
                        +{certificate.requirements.length - 3} more requirements
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* <div className="flex items-center justify-between pt-2">
                <Badge variant="secondary" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {certificate.quizQuestions.length} Questions
                </Badge>
              </div> */}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* {selectedCertificate && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Check className="h-5 w-5 text-green-600" />
            <span className="font-medium text-green-800">
              Selected: {selectedCertificate.title}
            </span>
          </div>
          <p className="text-sm text-green-700 mt-1">
            You can now proceed to start your certification journey.
          </p>
        </div>
      )} */}
    </div>
  );
}
